/**
 * SEDCAR waiver report — /classrooms/waiver-report
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download, ArrowBack } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import AppHeader from '../AppHeader';
import SessionSelector from '../SessionSelector';
import { classroomAPI, sessionAPI } from '../../services/api';
import { useClearOnSiteChange, useCurrentSiteId } from '../../hooks/useCurrentSiteId';

const statusChip = (status) => {
  switch (status) {
    case 'over_capacity': return <Chip label="Over capacity" size="small" color="error" />;
    case 'at_capacity': return <Chip label="At capacity" size="small" color="warning" />;
    default: return <Chip label="OK" size="small" color="success" />;
  }
};

function exportCsv(rows, sessionName) {
  const header = ['Classroom', 'Ratio', 'Licensed Max', 'Enrolled', 'Waiver Students', 'Approved', 'Pending', 'Status'];
  const lines = rows.map((r) => [
    r.classroom_name, r.class_ratio, r.licensed_max, r.enrolled,
    r.waiver_students, r.approved_waivers, r.pending_waivers, r.status,
  ].join(','));
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `waiver-report-${sessionName || 'session'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function WaiverReportPage() {
  const siteId = useCurrentSiteId();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useClearOnSiteChange(siteId, () => {
    setSessions([]);
    setCurrentSession(null);
    setRows([]);
    setLoading(true);
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([sessionAPI.getSessions(), sessionAPI.getCurrentSession()]).then(([sessRes, curRes]) => {
      if (cancelled) return;
      const list = Array.isArray(sessRes.data) ? sessRes.data : (sessRes.data?.results || []);
      setSessions(list);
      if (curRes.data?.id) setCurrentSession(curRes.data);
      else if (list.length) setCurrentSession(list.find((s) => s.is_active) || list[0]);
      else setCurrentSession(null);
    });
    return () => { cancelled = true; };
  }, [siteId]);

  const load = useCallback(async () => {
    if (!currentSession?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await classroomAPI.waiverReport(currentSession.id);
      setRows(res.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [currentSession?.id]);

  useEffect(() => { load(); }, [load]);

  const sessionLabel = currentSession?.display_name || currentSession?.name || '';

  return (
    <>
      <AppHeader title="Waiver Report" />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button component={RouterLink} to="/classrooms" startIcon={<ArrowBack />} size="small">Classrooms</Button>
            <Typography variant="h5">Waiver Report</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <SessionSelector
              sessions={sessions}
              currentSession={currentSession}
              onSessionChange={(id) => {
                const s = sessions.find((x) => String(x.id) === String(id));
                setCurrentSession(s || null);
              }}
            />
            <Button startIcon={<Download />} variant="outlined" disabled={!rows.length}
              onClick={() => exportCsv(rows, sessionLabel)}>
              Export CSV
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Classroom</TableCell>
                    <TableCell>Ratio</TableCell>
                    <TableCell>Licensed Max</TableCell>
                    <TableCell>Enrolled</TableCell>
                    <TableCell>Waiver students</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No classrooms in this session.</TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.classroom_id}>
                      <TableCell>{r.classroom_name}</TableCell>
                      <TableCell>{r.class_ratio}</TableCell>
                      <TableCell>{r.licensed_max}</TableCell>
                      <TableCell>{r.enrolled}</TableCell>
                      <TableCell>{r.waiver_students}</TableCell>
                      <TableCell>{r.approved_waivers}</TableCell>
                      <TableCell>{r.pending_waivers}</TableCell>
                      <TableCell>{statusChip(r.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </>
  );
}
